/* globals fbq */

// #region Imports
import merge from 'deepmerge';
import { setCookie, getCookie } from './helpers';

// #endregion

const overwriteMerge = (destinationArray, sourceArray, options) => sourceArray;

const COOKIE_PREFS = '_gatosec_prefs';

const DEFAULT_SETTINGS = {
  parentSelector: 'body',
  delay: 2000,
  expires: 365,

  categories: [
    {
      title: 'Site Preferences',
      value: 'preferences',
      description:
        'These are cookies that are related to your site preferences, e.g. remembering your username, site colours, etc.',
    },
    {
      title: 'Analytics',
      value: 'analytics',
      description: 'Cookies related to site visits, browser types, etc.',
    },
    {
      title: 'Marketing',
      value: 'marketing',
      description: 'Cookies related to marketing, e.g. newsletters, social media, etc',
    },
  ],

  popupTitle: 'Cookies & Privacy',
  popupMessage:
    'Cookies enable you to use shopping carts and to personalize your experience on our sites, tell us which parts of our websites people have visited, help us measure the effectiveness of ads and web searches, and give us insights into user behavior so we can improve our communications and products.',

  moreInfoLabel: 'More information',
  moreInfoLink: '/privacy-policy',

  settingsTitle: 'Select cookies to accept',

  buttonAcceptLabel: 'Accept Cookies',
  buttonAcceptSelectedLabel: 'Accept selected',
  buttonSettingsLabel: 'Settings',

  descriptionShowLabel: 'display description',
  descriptionHideLabel: 'hide description',

  requiredCookieLabel: 'Necessary',
  requiredCookieDescription: 'These are cookies that are essential for the website to work correctly.',

  onAccept: function () {},
};

export default class GatoSec {
  constructor(options) {
    this.settings = merge(DEFAULT_SETTINGS, options, { arrayMerge: overwriteMerge });

    // Elements
    this.elements = this._buildElements(this.settings);

    // Flags
    this.shown = false;
    this.settingsShown = false;

    // Binds
    this._handleWrapperClick = this._handleWrapperClick.bind(this);
    this._handleDocumentClick = this._handleDocumentClick.bind(this);
  }

  /**
   * Get user cookie preferences
   */
  getPreferences() {
    return JSON.parse(getCookie(COOKIE_PREFS));
  }

  /**
   * Check if user has accepted specified cookieType
   * @param {string} cookieType
   */
  getPreference(cookieType) {
    // const controlCookie = getControlCookie();
    const preferences = this.getPreferences();
    if (preferences !== false) {
      return preferences.indexOf(cookieType) !== -1;
    }
    return false;
  }

  /**
   * Save users cookie preferences (in a cookie!)
   * @param {Array} prefs user preferences set via checkboxes
   */
  _setPreferences(prefs) {
    setCookie(COOKIE_PREFS, encodeURIComponent(JSON.stringify(prefs)), this.settings.expires);
  }

  /**
   * Get current user preferences from checkboxes
   */
  _getPreferencesFromDOM() {
    return this.elements.checkboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value);
  }

  /**
   * Mirror user preferences (stored in cookie) to checkboxes checked prop
   * @param {Array} prefs user preferences retrieved from cookie
   */
  _setPreferencesInDOM(prefs) {
    prefs.forEach((value) =>
      this.elements.checkboxes
        .filter((checkbox) => checkbox.value === value)
        .forEach((checkbox) => (checkbox.checked = true))
    );
  }

  /**
   * Show popup
   */
  show() {
    if (this.getPreferences() !== false) this.showSettings();
    this.elements.wrapper.classList.add('show');
    this.shown = true;
  }

  /**
   * Hide popup
   */
  hide() {
    this.elements.wrapper.classList.remove('show');
    if (this.settingsShown) this.hideSettings();
    this.shown = false;
  }

  /**
   * Toggle popup shown state
   */
  toggle() {
    if (this.shown) this.hide();
    else this.show();
  }

  /**
   * Show popup settings
   */
  showSettings() {
    const { settingsContainer, checkboxes, buttons } = this.elements;

    // Set 'data-auto' to OFF for all. User can now select the cookies they want to accept.
    checkboxes.forEach((checkbox) => checkbox.setAttribute('data-auto', 'off'));

    buttons.settings.disabled = true;
    buttons.accept.textContent = this.settings.buttonAcceptSelectedLabel;
    settingsContainer.classList.add('show');

    this.settingsShown = true;
  }

  /**
   * Hide popup settings
   */
  hideSettings() {
    const { buttons, settingsContainer } = this.elements;

    settingsContainer.classList.remove('show');
    buttons.settings.disabled = false;
    buttons.accept.textContent = this.settings.buttonAcceptLabel;
    this._hideAllDescriptions();

    this.settingsShown = false;
  }

  /**
   * Toggle popup settings shown state
   */
  toggleSettings() {
    if (this.settingsShown) this.hideSettings();
    else this.showSettings();
  }

  /**
   * Build popup DOM
   * @param {{}} settings
   */
  _buildElements(settings) {
    const {
      buttonAcceptLabel,
      categories,
      settingsTitle,
      requiredCookieDescription,
      requiredCookieLabel,
      moreInfoLink,
      popupMessage,
      descriptionShowLabel,
      moreInfoLabel,
      buttonSettingsLabel,
      parentSelector,
      popupTitle,
    } = settings;

    // Add necessary cookies to cookies list
    const necessaryCookieType = {
      value: 'necessary',
      title: requiredCookieLabel,
      description: requiredCookieDescription,
    };

    const cookies = [necessaryCookieType].concat(categories);

    const list = document.createElement('UL');
    const checkboxes = [];
    const descriptions = [];

    // Generate list of cookie type checkboxes
    for (let index = 0; index < cookies.length; index++) {
      const { value, title, description } = cookies[index];

      if (title !== '' && value !== '') {
        const inputId = `gatosec-${value}`;
        const descId = `gatosec-${value}-desc`;

        // checkbox
        const input = document.createElement('INPUT');
        input.type = 'checkbox';
        input.id = inputId;
        input.name = 'gdpr[]';
        input.value = value;
        input.setAttribute('aria-describedby', descId);
        if (value === 'necessary') {
          input.checked = true;
          input.disabled = true;
        } else {
          input.setAttribute('data-auto', 'on');
        }
        checkboxes.push(input);

        // checkbox label
        const label = document.createElement('LABEL');
        label.setAttribute('for', inputId);
        label.textContent = title;

        // descriptions
        let descriptionToggle;
        let inputDescription;
        if (description !== false) {
          descriptionToggle = document.createElement('BUTTON');
          descriptionToggle.type = 'button';
          descriptionToggle.textContent = descriptionShowLabel;
          descriptionToggle.className = 'gatosec-desc-toggle';
          descriptionToggle.setAttribute('data-action', 'desc');
          descriptionToggle.setAttribute('data-target', descId);

          inputDescription = document.createElement('P');
          inputDescription.id = descId;
          inputDescription.className = 'gatosec-desc';
          inputDescription.innerHTML = description;

          descriptions.push(inputDescription);
        }

        // li
        const listItem = document.createElement('LI');
        [input, label].forEach((el) => listItem.appendChild(el));
        if (inputDescription) {
          [descriptionToggle, inputDescription].forEach((el) => listItem.appendChild(el));
        }

        // append everything to ul
        list.appendChild(listItem);
      }
    }

    const settingsContainerTitle = document.createElement('DIV');
    settingsContainerTitle.role = 'heading';
    settingsContainerTitle.setAttribute('aria-level', '2');
    settingsContainerTitle.className = 'gatosec-subtitle';
    settingsContainerTitle.textContent = settingsTitle;

    const settingsContainer = document.createElement('DIV');
    settingsContainer.className = 'gatosec-types';
    [settingsContainerTitle, list].forEach((el) => settingsContainer.appendChild(el));

    function makeBtn(action, label) {
      const button = document.createElement('BUTTON');
      button.type = 'button';
      button.textContent = label;
      button.setAttribute('data-action', action);
      return button;
    }
    const buttonSettings = makeBtn('settings', buttonSettingsLabel);
    const buttonAccept = makeBtn('accept', buttonAcceptLabel);

    const buttonsContainer = document.createElement('DIV');
    buttonsContainer.className = 'gatosec-buttons';
    [buttonSettings, buttonAccept].forEach((el) => buttonsContainer.appendChild(el));

    const boxTitle = document.createElement('DIV');
    boxTitle.role = 'heading';
    boxTitle.setAttribute('aria-level', '1');
    boxTitle.id = 'gatosec-title';
    boxTitle.className = 'gatosec-title';
    boxTitle.textContent = popupTitle;

    const boxMessage = document.createElement('p');
    boxMessage.innerHTML = `${popupMessage} <a href="${moreInfoLink}">${moreInfoLabel}</a>`;

    const wrapper = document.createElement('DIV');
    wrapper.className = 'gatosec';
    wrapper.setAttribute('role', 'region');
    wrapper.setAttribute('aria-labelledby', 'gatosec-title');
    [boxTitle, boxMessage, settingsContainer, buttonsContainer].forEach((el) => wrapper.appendChild(el));

    return {
      wrapper,
      parent: document.querySelector(parentSelector),
      settingsContainer: settingsContainer,
      checkboxes,
      descriptions,
      buttons: {
        accept: buttonAccept,
        settings: buttonSettings,
      },
    };
  }

  /**
   * Handle clicks inside whole document
   * @param {*} event
   */
  _handleDocumentClick(event) {
    const { target } = event;

    if (target.classList.contains('gatosec-open')) {
      event.preventDefault();
      this.show();
    }
  }

  /**
   * Handle clicks inside popup
   * @param {*} event
   */
  _handleWrapperClick(event) {
    const { target } = event;
    const action = target.getAttribute('data-action');
    switch (action) {
      case 'settings':
        this.showSettings();
        break;
      case 'accept':
        this._accept();
        break;
      case 'desc':
        this._toggleDescription(target);
        break;
      default:
        break;
    }
  }

  _hideAllDescriptions() {
    this.elements.descriptions.forEach((description) => description.classList.remove('show'));
  }

  /**
   * Toggle description shown state
   * @param {HTMLButtonElement} trigger clicked button
   */
  _toggleDescription(trigger) {
    const id = trigger.getAttribute('data-target');
    this.elements.descriptions
      .filter((description) => description.id === id)
      .forEach((description) => {
        if (description.classList.contains('show')) {
          description.classList.remove('show');
          trigger.textContent = this.settings.descriptionShowLabel;
        } else {
          description.classList.add('show');
          trigger.textContent = this.settings.descriptionHideLabel;
        }
      });
  }

  /**
   * Triggered when user accepts
   */
  _accept() {
    const { checkboxes } = this.elements;

    // If 'data-auto' is set to ON, tick all checkboxes
    // (user hasn't clicked the advanced settings button)
    checkboxes
      .filter((checkbox) => checkbox.getAttribute('data-auto') === 'on')
      .forEach((checkbox) => (checkbox.checked = true));

    this._setPreferences(this._getPreferencesFromDOM());

    this.hide();

    // Run callback function
    this.settings.onAccept.call(this);
  }

  /**
   * Add or remove event listeners
   * @param {String} addOrRemove
   */
  _setEvents(addOrRemove) {
    const fn = `${addOrRemove}EventListener`;
    this.elements.wrapper[fn]('click', this._handleWrapperClick);
    document.body[fn]('click', this._handleDocumentClick);
  }

  destroy() {
    const { wrapper, parent } = this.elements;
    this._setEvents('remove');
    parent.removeChild(wrapper);

    this.shown = false;
    this.settingsShown = false;
  }

  init() {
    const { wrapper, parent } = this.elements;
    const preferences = this.getPreferences();

    // Insert template and register events
    parent.appendChild(wrapper);
    this._setEvents('add');

    if (!preferences) {
      setTimeout(() => this.show(), this.settings.delay);
    } else {
      this._setPreferencesInDOM(preferences);
      this.settings.onAccept.call(this);
    }
  }
}
